import { useMarkTourViewedMutation } from '@/app/redux/endpoints/user';
import { useUser } from '@/app/redux/hooks/user';
import { QuestionIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useEffect } from 'react';

const TOUR_KEY = 'customLevel';

export default function CustomLevelEditorTour() {
  const { user } = useUser();
  const [markTourViewed] = useMarkTourViewedMutation();

  function startTour() {
    if (user && !user.viewedTours?.includes(TOUR_KEY)) {
      markTourViewed({ userId: user.uid, tourKey: TOUR_KEY });
    }
    const driverObj = driver({
      showProgress: true,
      steps: [
        {
          popover: {
            title: 'Custom levels',
            description:
              "A custom level lets you hand-pick the words, graphemes, and sentences your learner practises across each game. Let's take a quick look around.",
          },
        },
        {
          element: '#custom-level-title',
          popover: {
            title: 'Level name',
            description: 'Give your level a name — click to edit it.',
          },
        },
        {
          element: '#custom-level-learners',
          popover: {
            title: 'Learners',
            description:
              'Select learners to assign this level to. It will appear in their main menu as “Start Custom Level.”',
          },
        },
        {
          element: '#custom-level-games',
          popover: {
            title: 'Games',
            description: 'Enable and configure games for this level.',
          },
        },
        {
          element: '#game-row-mysticMatch',
          popover: {
            title: 'Toggle a game',
            description:
              'Use the checkbox to enable a game for your level. Each card shows a summary of its configuration.',
          },
        },
        {
          element: '#game-row-mysticMatch .cursor-grab',
          popover: {
            title: 'Reorder games',
            description:
              'Drag the handle to reorder games. The order here is the order learners will play them.',
          },
        },
        {
          element: '#grapheme-selector',
          popover: {
            title: 'Select items',
            description:
              'Search and select content for each game — drag the content chips to reorder them.',
          },
        },
      ],
    });
    driverObj.drive();
  }

  useEffect(() => {
    if (user && !user.viewedTours?.includes(TOUR_KEY)) {
      startTour();
    }
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" onClick={startTour}>
            <QuestionIcon size={18} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end">
          Show me around
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
